/**
 * Invoicing register model (24 #4, #5, #6) — pure functions: every base and variation (VO) invoice as one
 * row with its milestone, amount, submitted date + age (aging buckets), QuickBooks state, the register
 * views (Awaiting payment / Ready to raise / Not raised / Paid / All) and the payment-reminder text.
 *
 * "Ready to raise" (24 #4 saved view, 24 #8): base milestones not invoiced yet whose work is done in Execution —
 * `readyIds` (a Set or Map of invoice ids from GET /invoicing/ready-to-bill). They also lead "Not raised".
 *
 * Submitted date: `submitted_at` is recorded by the API from wave 2 on. Older invoices only have
 * `updated_at` (moved by any edit) — those rows say so (`submittedSource: 'last_change'`) instead of
 * presenting the date as exact.
 */
import { ageDays, formatDate, formatMoney } from '@/lib/format';
import { buildInvoiceRows, MILESTONES } from './invoicingModel';
import { deletionRequestOf } from './invoiceDeletionModel';

const text = (value) => String(value ?? '').trim();

const MILESTONE_LABEL = Object.fromEntries(MILESTONES.flatMap((milestone) => milestone.matches.map((match) => [match, milestone.label])));
export const milestoneLabel = (type) => MILESTONE_LABEL[type] || type;

export const AGING = [
  { key: 'over90', label: 'Over 90 d', tone: 'red' },
  { key: 'd31', label: '31–90 d', tone: 'amber' },
  { key: 'd0', label: '0–30 d', tone: 'gray' },
];
export const agingBucket = (days) => {
  if (days === null || days === undefined) return null;
  if (days > 90) return 'over90';
  if (days > 30) return 'd31';
  return 'd0';
};

/** The QuickBooks number shown in the register ("QB #1098"), or '' when the invoice is not synced. */
export const quickBooksNumber = (invoice) => text(invoice?.quickbooks?.doc_number) || text(invoice?.quickbooks?.invoice_id);

/**
 * Base + VO invoices as register rows (on top of buildInvoiceRows: same buckets, split and filters).
 * kind: 'base' | 'vo'. ageDays only for invoices awaiting payment.
 */
export function buildRegisterRows(invoices, variationInvoices, projectMap, { now } = {}) {
  const tag = (rows, kind, source) => rows.map((row, index) => {
    const invoice = source[index] || row.invoice;
    const recorded = text(invoice.submitted_at);
    const submittedOn = recorded || (row.bucket === 'awaiting' ? text(invoice.updated_at) || text(invoice.created_at) : '');
    const days = row.bucket === 'awaiting' && submittedOn ? ageDays(submittedOn, { now }) : null;
    const types = row.types.length ? row.types : [];
    const pct = invoice.invoice_type_percentages || {};
    const split = types.length > 1
      ? types.map((type) => (Number.isFinite(Number(pct[type])) && pct[type] !== undefined ? `${Number(pct[type])}` : `${Math.round(100 / types.length)}`)).join(' / ')
      : '';
    return {
      ...row,
      key: `${kind}-${row.id}`,
      kind,
      milestone: types.map(milestoneLabel).join(' + ') || 'No milestone',
      split,
      submittedOn: row.bucket === 'notStarted' ? '' : submittedOn,
      submittedSource: recorded ? 'recorded' : submittedOn ? 'last_change' : null,
      ageDays: days !== null && days >= 0 ? days : (days === null ? null : 0),
      aging: row.bucket === 'awaiting' ? agingBucket(days !== null ? Math.max(0, days) : null) : null,
      paidOn: text(invoice.paid_at) || (row.bucket === 'received' ? text(invoice.updated_at) : ''),
      qbNumber: quickBooksNumber(invoice),
      variationId: text(invoice.variation_id),
      // BUG-006: an open "please delete" waiting for an Admin (the row stays in every total until approved)
      deletionRequest: deletionRequestOf(invoice),
    };
  });
  const base = Array.isArray(invoices) ? invoices : [];
  const vo = Array.isArray(variationInvoices) ? variationInvoices : [];
  return [
    ...tag(buildInvoiceRows(base, projectMap), 'base', base),
    ...tag(buildInvoiceRows(vo, projectMap), 'vo', vo),
  ];
}

export const REGISTER_VIEWS = [
  { key: 'awaiting', label: 'Awaiting payment' },
  { key: 'ready', label: 'Ready to raise' },
  { key: 'notStarted', label: 'Not raised' },
  { key: 'received', label: 'Paid' },
  { key: 'all', label: 'All invoices' },
];

// BUG-006: rows someone asked to delete, waiting for an Admin — a view of its own, shown while there are any.
export const DELETION_VIEW = { key: 'deletion', label: 'Deletion requests' };
export const isRegisterView = (key) => key === DELETION_VIEW.key || REGISTER_VIEWS.some((view) => view.key === key);

/** A row with an open deletion request (BUG-006). */
export const isDeletionRequested = (row) => Boolean(row?.deletionRequest);

const byText = (a, b) => a.localeCompare(b, undefined, { numeric: true });

/** A base milestone not invoiced yet whose work is finished (readyIds: Set / Map of invoice ids, or null). */
export const isReadyRow = (row, readyIds) => Boolean(readyIds && typeof readyIds.has === 'function'
  && row.kind === 'base' && row.bucket === 'notStarted' && readyIds.has(row.id));

/** Rows of one register view, sorted the way that view is worked through. */
export function registerView(rows, { view = 'awaiting', kind = 'all', aging = null, readyIds = null } = {}) {
  const list = rows.filter((row) => {
    if (kind !== 'all' && row.kind !== kind) return false;
    if (view === 'ready') return isReadyRow(row, readyIds);
    if (view === 'deletion') return isDeletionRequested(row);
    if (view !== 'all' && row.bucket !== view) return false;
    if (aging && row.aging !== aging) return false;
    return true;
  });
  if (view === 'awaiting') {
    // Oldest first: whoever has waited longest is called first; unknown dates last.
    return list.sort((a, b) => ((b.ageDays ?? -1) - (a.ageDays ?? -1)) || (b.amount - a.amount));
  }
  if (view === 'received') return list.sort((a, b) => byText(b.paidOn || '', a.paidOn || '') || byText(a.projectName, b.projectName));
  // Oldest request first: it has waited longest for a decision.
  if (view === 'deletion') {
    return list.sort((a, b) => byText(a.deletionRequest.requested_at || '', b.deletionRequest.requested_at || '') || byText(a.projectName, b.projectName));
  }
  // Biggest finished work first.
  if (view === 'ready') return list.sort((a, b) => (b.amount - a.amount) || byText(a.projectName, b.projectName));
  // What can be invoiced today leads the scheduled milestones.
  if (view === 'notStarted') {
    return list.sort((a, b) => (isReadyRow(b, readyIds) - isReadyRow(a, readyIds))
      || byText(a.projectName, b.projectName) || byText(a.milestone, b.milestone));
  }
  return list.sort((a, b) => byText(b.createdDay || '', a.createdDay || '') || byText(a.projectName, b.projectName));
}

/** Counts per view / kind and the aging buckets of the awaiting invoices (count + amount). */
export function registerCounts(rows, { readyIds = null } = {}) {
  const count = (predicate) => rows.filter(predicate).length;
  const awaiting = rows.filter((row) => row.bucket === 'awaiting');
  const viewCount = (key) => {
    if (key === 'all') return rows.length;
    if (key === 'ready') return count((row) => isReadyRow(row, readyIds));
    return count((row) => row.bucket === key);
  };
  return {
    views: Object.fromEntries(REGISTER_VIEWS.map((view) => [view.key, viewCount(view.key)])),
    deletion: count(isDeletionRequested),
    kinds: { all: rows.length, base: count((row) => row.kind === 'base'), vo: count((row) => row.kind === 'vo') },
    aging: Object.fromEntries(AGING.map((bucket) => {
      const items = awaiting.filter((row) => row.aging === bucket.key);
      return [bucket.key, { count: items.length, amount: items.reduce((sum, row) => sum + row.amount, 0) }];
    })),
    awaitingCount: awaiting.length,
    awaitingAmount: awaiting.reduce((sum, row) => sum + row.amount, 0),
    over90: awaiting.filter((row) => row.aging === 'over90').length,
    oldest: awaiting.reduce((best, row) => (row.ageDays !== null && (!best || row.ageDays > best.ageDays) ? row : best), null),
    estimatedDates: awaiting.some((row) => row.submittedSource === 'last_change'),
  };
}

/** Raised invoices (Submitted / Payment Received, amount > 0) without a QuickBooks number. */
export const unsyncedRows = (rows) => rows.filter((row) => row.bucket !== 'notStarted' && row.amount > 0 && !row.qbNumber);

/** Newest QuickBooks sync among the invoices ('' when none was ever synced). */
export function lastQuickBooksSync(invoices) {
  return (Array.isArray(invoices) ? invoices : [])
    .map((invoice) => text(invoice?.quickbooks?.synced_at))
    .filter(Boolean)
    .sort()
    .pop() || '';
}

/**
 * A short, polite payment reminder the accounts team can paste into an email or WhatsApp. It goes to the customer,
 * so it only states what is recorded: an older invoice whose submitted day is an estimate (the day it was last
 * changed, "≈" in the register) says neither the date nor the age, and with no client it greets "Sir / Madam".
 */
export function paymentReminder(row, { now } = {}) {
  const who = text(row.client) || 'Sir / Madam';
  const ref = row.reference ? `invoice ${row.reference}` : 'our invoice';
  const exactDay = row.submittedOn && row.submittedSource !== 'last_change';
  const when = exactDay ? ` submitted on ${formatDate(row.submittedOn, { now })}` : '';
  const age = exactDay && row.ageDays ? ` (${row.ageDays} days ago)` : '';
  return [
    `Dear ${who},`,
    '',
    `This is a friendly reminder that ${ref} for ${row.projectName || 'the project'} — ${row.milestone} — of ${formatMoney(row.amount)}${when}${age} is still awaiting payment.`,
    'Could you please let us know when we can expect it? If it has already been paid, please share the payment reference.',
    '',
    'Thank you.',
  ].join('\n');
}

/** Where a register row opens: the project's Invoicing tab (VO invoices live on the Variations tab). */
export const rowLink = (row) => (row.projectId ? `/projects/${row.projectId}?tab=${row.kind === 'vo' ? 'variations' : 'invoicing'}` : null);

